// The schedule builder of the routine editor (SPEC §15 Schedule): rhythm, time, weekdays, day of the
// month, every N hours / minutes with an optional time window and "weekdays only", start and end
// dates, and a live French preview ("Routine · le lundi à 09:00 · prochain passage …").
import { h, uid, setText, switchToggle, field, input } from '../../dom.js';
import { t, DAYS, DAYS_SHORT } from '../../i18n/fr.js';
import { formatDateTime } from '../../format.js';
import {
  SCHEDULE_KINDS, WEEK_ORDER, MIN_INTERVAL_MINUTES, MAX_INTERVAL_MINUTES, MAX_HOURLY_EVERY, defaultBuilder,
  builderToSchedule, validateSchedule, describeSchedule, nextRun, toggleDay, sortDays,
} from './schedule.js';
import './strings.js';

const TIMED = new Set(['daily', 'weekdays', 'weekly', 'monthly']);

function intValue(el, fallback) {
  const n = Number.parseInt(String(el.value ?? ''), 10);
  return Number.isInteger(n) ? n : fallback;
}

/**
 * Creates the builder.
 * @param {object} initial a Builder (see schedule.js)
 * @param {{onChange?: (builder: object) => void, now?: () => number}} [opts]
 * @returns {{el: HTMLElement, readonly builder: object, setBuilder(b: object): void, problems(): string[],
 *   focusFirstInvalid(): void}}
 */
export function createScheduleBuilder(initial, { onChange, now = () => Date.now() } = {}) {
  let b = { ...defaultBuilder(), ...(initial || {}) };

  const kindSelect = h('select', { class: 'input select' },
    SCHEDULE_KINDS.map((k) => h('option', { value: k }, t(`auto.re.kind.${k}`))));
  const atInput = input({ type: 'time', step: 60, required: true });
  const dayButtons = new Map();
  const daysLabelId = uid('days');
  const daysRow = h('div', { class: 'auto-days', role: 'group', 'aria-labelledby': daysLabelId });
  for (const d of WEEK_ORDER) {
    const btn = h('button', {
      type: 'button', class: 'auto-day', 'aria-pressed': 'false', 'aria-label': DAYS[d], title: DAYS[d],
      onClick: () => { b = { ...b, days: toggleDay(b.days, d) }; sync(true); },
    }, DAYS_SHORT[d].replace('.', ''));
    dayButtons.set(d, btn);
    daysRow.appendChild(btn);
  }
  const domInput = input({ type: 'number', min: 1, max: 31, step: 1, inputmode: 'numeric' });
  const hoursInput = input({ type: 'number', min: 1, max: MAX_HOURLY_EVERY, step: 1, inputmode: 'numeric' });
  const minutesInput = input({ type: 'number', min: MIN_INTERVAL_MINUTES, max: MAX_INTERVAL_MINUTES, step: 5, inputmode: 'numeric' });
  const fromInput = input({ type: 'time', step: 60 });
  const toInput = input({ type: 'time', step: 60 });
  const weekdaysSwitch = switchToggle(t('auto.re.weekdaysOnly'), { onChange: (v) => { b = { ...b, weekdaysOnly: v }; sync(true); } });
  const startInput = input({ type: 'date' });
  const endInput = input({ type: 'date' });
  const preview = h('p', { class: 'auto-sched-preview', 'aria-live': 'polite' });

  const kindField = field(t('auto.re.kind'), kindSelect);
  const atField = field(t('auto.re.at'), atInput);
  const daysField = h('div', { class: 'field' }, h('span', { class: 'field-label', id: daysLabelId }, t('auto.re.days')), daysRow);
  const domField = field(t('auto.re.dayOfMonth'), domInput, { hint: t('auto.re.dayOfMonthHint') });
  const hoursField = field(t('auto.re.everyHours'), hoursInput);
  const minutesField = field(t('auto.re.everyMinutes'), minutesInput, { hint: t('auto.re.everyMinutesHint') });
  const fromField = field(t('auto.re.from'), fromInput, { optional: true });
  const toField = field(t('auto.re.to'), toInput, { optional: true });
  const windowRow = h('div', { class: 'auto-sched-row' }, hoursField, minutesField, fromField, toField);
  const windowHint = h('p', { class: 'field-hint' }, t('auto.re.windowHint'));
  const windowBox = h('div', { class: 'auto-sched-window' }, windowRow, windowHint, weekdaysSwitch);
  const boundsRow = h('div', { class: 'auto-sched-row' },
    field(t('auto.re.start'), startInput, { optional: true }), field(t('auto.re.end'), endInput, { optional: true }));

  const legendId = uid('when');
  const el = h('fieldset', { class: 'auto-sched', 'aria-labelledby': legendId },
    h('legend', { class: 'mono-label auto-legend', id: legendId }, t('auto.re.when')),
    h('div', { class: 'auto-sched-row' }, kindField, atField),
    daysField, domField, windowBox, boundsRow, preview);

  function readInputs() {
    b = {
      ...b,
      kind: kindSelect.value,
      at: atInput.value ? atInput.value.slice(0, 5) : '',
      dayOfMonth: intValue(domInput, b.dayOfMonth),
      everyHours: intValue(hoursInput, b.everyHours),
      everyMinutes: intValue(minutesInput, b.everyMinutes),
      from: fromInput.value ? fromInput.value.slice(0, 5) : '',
      to: toInput.value ? toInput.value.slice(0, 5) : '',
      start: startInput.value || '',
      end: endInput.value || '',
    };
  }

  function writeInputs() {
    kindSelect.value = b.kind;
    atInput.value = b.at || '';
    domInput.value = String(b.dayOfMonth ?? '');
    hoursInput.value = String(b.everyHours ?? '');
    minutesInput.value = String(b.everyMinutes ?? '');
    fromInput.value = b.from || '';
    toInput.value = b.to || '';
    weekdaysSwitch.input.checked = !!b.weekdaysOnly;
    startInput.value = b.start || '';
    endInput.value = b.end || '';
  }

  function sync(emit) {
    const kind = b.kind;
    atField.hidden = !TIMED.has(kind);
    daysField.hidden = kind !== 'weekly';
    domField.hidden = kind !== 'monthly';
    windowBox.hidden = kind !== 'hourly' && kind !== 'interval';
    hoursField.hidden = kind !== 'hourly';
    minutesField.hidden = kind !== 'interval';
    const days = new Set(sortDays(b.days || []));
    for (const [d, btn] of dayButtons) {
      btn.setAttribute('aria-pressed', String(days.has(d)));
      btn.classList.toggle('is-on', days.has(d));
    }
    const schedule = builderToSchedule(b);
    const problems = validateSchedule(schedule);
    if (problems.length) {
      setText(preview, problems[0]);
      preview.classList.add('is-error');
    } else {
      const next = nextRun(schedule, now());
      const parts = [t('auto.re.preview', { description: describeSchedule(schedule, { now: now() }) })];
      parts.push(next ? t('auto.re.previewNext', { when: formatDateTime(next, now()) }) : t('auto.re.previewEnded'));
      setText(preview, parts.join(' · '));
      preview.classList.remove('is-error');
    }
    if (emit) onChange?.(b);
  }

  for (const ctl of [kindSelect, atInput, domInput, hoursInput, minutesInput, fromInput, toInput, startInput, endInput]) {
    ctl.addEventListener('input', () => { readInputs(); sync(true); });
    ctl.addEventListener('change', () => { readInputs(); sync(true); });
  }

  writeInputs();
  sync(false);

  return {
    el,
    get builder() { return { ...b, days: [...(b.days || [])] }; },
    setBuilder(next) {
      b = { ...defaultBuilder(), ...next };
      writeInputs();
      sync(true);
    },
    problems: () => validateSchedule(builderToSchedule(b)),
    focusFirstInvalid() {
      const s = builderToSchedule(b);
      if (TIMED.has(s.kind) && !/^\d{2}:\d{2}$/.test(s.at || '')) atInput.focus();
      else if (s.kind === 'weekly' && !(s.days || []).length) dayButtons.get(1)?.focus();
      else if (s.kind === 'monthly') domInput.focus();
      else if (s.kind === 'hourly') hoursInput.focus();
      else if (s.kind === 'interval') minutesInput.focus();
      else kindSelect.focus();
    },
  };
}
